import React, { useEffect, useState } from "react";
import OperationsPage from "./OperationsPage";
import { WeekNavigator, startOfWeek, fmtDate } from "../../components/shared/Navigators";
import { formatTime12 } from "../../lib/format";
import { Clock } from "lucide-react";

const STATUS = ["Scheduled", "Confirmed", "Completed", "Cancelled", "Rescheduled", "No-Show", "Waiting on Response"];
const TYPES = ["Call", "Meeting", "Follow-Up Call", "Intro Call", "Sales Call", "Referral Partner Call", "Client Meeting", "Lender Call", "Realtor Call", "Internal Meeting", "Networking Meeting", "Other"];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const FIELDS = [
  { name: "date", label: "Date", type: "date" },
  { name: "day", label: "Day", type: "select", options: DAYS },
  { name: "start_time", label: "Start Time", type: "time" },
  { name: "end_time", label: "End Time", type: "time" },
  { name: "type", label: "Type", type: "select", options: TYPES },
  { name: "company", label: "Company" },
  { name: "individual", label: "Individual(s)" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone Number" },
  { name: "meeting_link", label: "Meeting Link" },
  { name: "location", label: "Location" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "follow_up", label: "Follow Up", full: true },
  { name: "follow_up_date", label: "Follow Up Date", type: "date" },
  { name: "status", label: "Status", type: "select", options: STATUS },
  { name: "priority", label: "Priority", type: "select", options: ["Normal", "Priority"] },
];

const CATS = [
  { key: "Monday", color: "#0090FF", fields: FIELDS },
  { key: "Tuesday", color: "#0090FF", fields: FIELDS },
  { key: "Wednesday", color: "#0090FF", fields: FIELDS },
  { key: "Thursday", color: "#0090FF", fields: FIELDS },
  { key: "Friday", color: "#0090FF", fields: FIELDS },
  { key: "Saturday", color: "#707070", fields: FIELDS },
  { key: "Sunday", color: "#707070", fields: FIELDS },
].map((c) => ({ ...c, label: c.key }));

const COLUMNS = [
  { key: "date", label: "Date", className: "font-mono-rw text-xs" },
  { key: "start_time", label: "Start", className: "font-mono-rw text-xs", render: formatTime12 },
  { key: "end_time", label: "End", className: "font-mono-rw text-xs", render: formatTime12 },
  { key: "type", label: "Type" },
  { key: "company", label: "Company" },
  { key: "individual", label: "Individual(s)" },
  { key: "location", label: "Location" },
  { key: "follow_up", label: "Follow-up" },
  { key: "status", label: "Status" },
  { key: "priority", label: "Pri." },
];

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function CallSchedule() {
  const [value, setValue] = useState(fmtDate(startOfWeek(new Date())));
  const [showNow, setShowNow] = useState(
    () => (localStorage.getItem("rw_now_line") ?? "on") !== "off"
  );
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    localStorage.setItem("rw_now_line", showNow ? "on" : "off");
  }, [showNow]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const decorate = (name) => {
    const i = DAYS.indexOf(name);
    if (i < 0) return name;
    const d = new Date(value + "T12:00:00");
    d.setDate(d.getDate() + i);
    return `${name}, ${MONTH_NAMES[d.getMonth()]} ${ordinal(d.getDate())}`;
  };

  // Inject a synthetic "now" row only into today's section, and only if it
  // belongs to the currently displayed week.
  const todayDow = new Date().toLocaleDateString("en-US", { weekday: "long" });
  const todayIso = new Date().toISOString().slice(0, 10);
  const weekStart = value;
  const weekEnd = (() => {
    const d = new Date(value + "T12:00:00");
    d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  })();
  const todayInWeek = todayIso >= weekStart && todayIso <= weekEnd;

  const injectRowFor = (catName) => {
    if (!showNow || !todayInWeek || catName !== todayDow) return null;
    const d = new Date(nowTick);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return {
      _now: true,
      _nowLabel: formatTime12(`${hh}:${mm}`),
      start_time: `${hh}:${mm}`,
      id: "__now__",
    };
  };

  const nowToggle = (
    <button
      type="button"
      onClick={() => setShowNow((v) => !v)}
      aria-pressed={showNow}
      data-testid="now-line-toggle"
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-[4px] border transition-colors ${
        showNow
          ? "bg-[#E5484D] text-white border-[#E5484D] hover:bg-[#c93a3f]"
          : "bg-white text-neutral-600 border-[var(--rw-border)] hover:bg-[var(--rw-surface-2)]"
      }`}
      title={showNow ? "Hide “Now” indicator" : "Show “Now” indicator"}
    >
      <Clock size={12} />
      Now {showNow ? "on" : "off"}
    </button>
  );

  return (
    <OperationsPage
      kind="schedule"
      title="Monthly Call Schedule"
      eyebrow="Operations"
      dot="var(--rw-dot-schedule)"
      description="Weekly schedule — every call, meeting and appointment grouped by day."
      scope={{
        type: "week",
        value,
        setValue,
        Navigator: <WeekNavigator value={value} onChange={setValue} />,
      }}
      defaultCategories={CATS}
      getFieldsFor={() => FIELDS}
      columns={COLUMNS}
      decorateTitle={decorate}
      sortRowsBy={(a, b) => (a.start_time || "99:99").localeCompare(b.start_time || "99:99")}
      extraHeaderActions={nowToggle}
      injectRowFor={injectRowFor}
    />
  );
}
